import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  DestroyRef,
  NgZone,
  ViewContainerRef,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from 'projects/bp-core/src/lib/services/portal/auth.service';
import { AsesorIaLoader } from '../asesor-ia.loader';
import { AsesorContextoService } from '../services/asesor-contexto.service';
// por su ruta: el index de ai-chat trae el AI SDK y el montaje va en el bundle inicial
import { BpAgentLauncherComponent } from '@bp-core/src/lib/ai-chat/components/launcher/bp-agent-launcher.component';
import { ASESOR_LANZADOR } from '../asesor-ia.models';

/** Espera tras un cambio de selección antes de mirarla (la persona sigue arrastrando). */
const MS_SELECCION = 220;
/** Largo mínimo de una selección que puede terminar en "Preguntar al asesor". */
const MIN_SELECCION = 3;

/**
 * Punto de montaje global y liviano del Asesor IA (va en `app.component.html`, así sobrevive a la navegación).
 * Al arrancar solo muestra el botón flotante; el chat (bp-core, `ai`, la captura de pantalla y el asesor que usa el
 * portal) se descarga con la intención de usarlo y se monta aquí mismo (T056):
 * - al pasar el mouse o enfocar el botón empieza la descarga; al presionarlo, se abre el panel apenas llega;
 * - una selección de texto también lo descarga, para ofrecer "Preguntar al asesor";
 * - "Mis conversaciones" lo descarga al entrar.
 */
@Component({
  selector: 'asesor-montaje',
  standalone: true,
  imports: [BpAgentLauncherComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'as-root' },
  template: `
    @if (!montado() && visible()) {
      <bp-agent-launcher [label]="lanzador.label" [closeLabel]="lanzador.closeLabel" [zIndex]="lanzador.zIndex"
                         [bottomOffset]="ctx.bordeInferior()" [busy]="esperando()" (intent)="loader.precargar()" (activate)="abrir()" />
    }
    <ng-container #anfitrion />
  `,
  styleUrl: './asesor-montaje.component.scss',
})
export class AsesorMontajeComponent {
  readonly lanzador = ASESOR_LANZADOR;
  readonly loader = inject(AsesorIaLoader);
  readonly ctx = inject(AsesorContextoService);
  private readonly auth = inject(AuthService);
  private readonly doc = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  private readonly anfitrion = viewChild.required('anfitrion', { read: ViewContainerRef });

  private readonly usuario = toSignal(this.auth.userSubject$, { initialValue: undefined });
  private raiz: ComponentRef<unknown> | null = null;

  /** El panel ya cargado tomó el control del botón. */
  readonly montado = signal(false);
  /** La persona abrió el chat y la descarga todavía no termina. */
  readonly pedido = signal(false);
  readonly esperando = computed(() => this.pedido() && this.loader.estado() === 'cargando');

  /** Mismas reglas que el panel cargado: layout principal, usuario autenticado y fuera de "Mis conversaciones". */
  readonly visible = computed(
    () =>
      this.loader.disponible() === true &&
      !!this.usuario() &&
      this.ctx.enLayoutPrincipal() &&
      this.ctx.pantalla().tipo !== 'conversaciones',
  );

  constructor() {
    // Intercom: su botón se oculta mientras el botón del asesor está a la vista (después lo maneja el panel cargado)
    effect(() => {
      const mostrar = this.visible() && !this.montado();
      untracked(() => {
        if (!this.montado()) this.doc.body.classList.toggle('as-activo', mostrar);
      });
    });
    // en cuanto la parte diferida está lista (por cualquier motivo), se monta el panel
    effect(() => {
      if (this.loader.estado() === 'listo') untracked(() => this.montar());
    });
    // si falla la descarga, el botón vuelve a su estado normal y otro clic reintenta
    effect(() => {
      if (this.loader.estado() === 'error') untracked(() => this.pedido.set(false));
    });
    this.escucharSeleccion();
  }

  abrir(): void {
    this.pedido.set(true);
    this.loader.cargar().catch(() => undefined);
  }

  private montar(): void {
    const cargado = this.loader.cargado;
    if (this.raiz || !cargado) return;
    this.raiz = this.anfitrion().createComponent(cargado.raiz, { environmentInjector: cargado.injector });
    this.raiz.setInput('continuaLanzador', this.visible());
    // la vista nueva se revisa en esta misma pasada: el botón liviano sale y el del panel entra sin parpadeo
    this.montado.set(true);
    if (this.pedido()) {
      this.pedido.set(false);
      cargado.abrirPanel();
    }
  }

  /** Una selección de texto fuera de campos y del propio chat descarga el chat, que ofrece "Preguntar al asesor". */
  private escucharSeleccion(): void {
    const destroy = inject(DestroyRef);
    this.zone.runOutsideAngular(() => {
      let t: ReturnType<typeof setTimeout> | undefined;
      const revisar = () => {
        if (this.montado() || !this.visible()) return;
        const sel = this.doc.getSelection();
        const texto = sel?.toString().trim() ?? '';
        const nodo = sel?.anchorNode instanceof Element ? sel.anchorNode : sel?.anchorNode?.parentElement;
        if (texto.length >= MIN_SELECCION && nodo && !nodo.closest('.as-root, input, textarea, [contenteditable="true"]')) {
          this.zone.run(() => this.loader.precargar());
        }
      };
      const alCambiar = () => {
        clearTimeout(t);
        t = setTimeout(revisar, MS_SELECCION);
      };
      this.doc.addEventListener('selectionchange', alCambiar);
      destroy.onDestroy(() => {
        clearTimeout(t);
        this.doc.removeEventListener('selectionchange', alCambiar);
      });
    });
  }
}
